import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus } from 'lucide-react';
import { StageEditor } from './StageEditor';

const INITIAL_FORM = {
  title: '', client_name: '', address: '', scope_of_work: '',
  wood_type: '', cabinet_style: '', finish: '', target_completion_date: '', description: ''
};

const INPUT_CLASS = "bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl";

export function CreateProjectDialog({ open, onOpenChange, onCreate }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [stages, setStages] = useState([]);
  const [asQuote, setAsQuote] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, stages, status: asQuote ? 'quote' : 'active' };
    if (!payload.target_completion_date) payload.target_completion_date = null;
    const success = await onCreate(payload);
    if (success) {
      setForm(INITIAL_FORM);
      setStages([]);
      setAsQuote(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl" data-testid="add-project-button">
          <Plus className="w-4 h-4 mr-2" />New Project
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#23272E] border-[#33383F] rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#F0EDE8] font-bold tracking-tight">Create New Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#F0EDE8] font-medium text-sm">Project Name</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Kitchen Cabinets" required className={INPUT_CLASS} data-testid="project-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#F0EDE8] font-medium text-sm">Customer Name</Label>
              <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                placeholder="John Smith" required className={INPUT_CLASS} data-testid="project-client-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#F0EDE8] font-medium text-sm">Address</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="123 Main St, City, State" className={INPUT_CLASS} data-testid="project-address-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#F0EDE8] font-medium text-sm">Scope of Work</Label>
            <Textarea value={form.scope_of_work} onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })}
              placeholder="Full kitchen remodel, 24 cabinets..."
              className={`${INPUT_CLASS} min-h-[70px]`} data-testid="project-scope-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#F0EDE8] font-medium text-sm">Type of Wood</Label>
              <Input value={form.wood_type} onChange={(e) => setForm({ ...form, wood_type: e.target.value })}
                placeholder="Oak, Maple, Walnut..." className={INPUT_CLASS} data-testid="project-wood-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#F0EDE8] font-medium text-sm">Cabinet Style</Label>
              <Input value={form.cabinet_style} onChange={(e) => setForm({ ...form, cabinet_style: e.target.value })}
                placeholder="Shaker, Flat Panel..." className={INPUT_CLASS} data-testid="project-style-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[#F0EDE8] font-medium text-sm">Finish</Label>
              <Input value={form.finish} onChange={(e) => setForm({ ...form, finish: e.target.value })}
                placeholder="Natural, Stained, Painted..." className={INPUT_CLASS} data-testid="project-finish-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#F0EDE8] font-medium text-sm">Target Completion Date</Label>
              <Input type="date" value={form.target_completion_date}
                onChange={(e) => setForm({ ...form, target_completion_date: e.target.value })}
                className={INPUT_CLASS} data-testid="project-date-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#F0EDE8] font-medium text-sm">Notes</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Additional notes..."
              className={`${INPUT_CLASS} min-h-[60px]`} data-testid="project-notes-create-input" />
          </div>
          <StageEditor stages={stages} onChange={setStages} />
          <div className="flex items-center gap-2 pt-2">
            <button type="button" onClick={() => setAsQuote(!asQuote)}
              className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-colors
                ${asQuote ? 'bg-[#7C3AED] border-[#7C3AED]' : 'border-[#33383F] hover:border-[#7C3AED]'}`}
              data-testid="save-as-quote-toggle">
              {asQuote && <span className="text-white text-xs font-bold">✓</span>}
            </button>
            <label className="text-sm text-[#9CA3AF] cursor-pointer" onClick={() => setAsQuote(!asQuote)}>
              Save as Quote (potential project, not active yet)
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
              className="border-[#33383F] text-[#9CA3AF] hover:bg-[#2C3038] rounded-xl">Cancel</Button>
            <Button type="submit" className={`rounded-xl ${asQuote ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white' : 'bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white'}`}
              data-testid="create-project-submit">{asQuote ? 'Save Quote' : 'Create Project'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
